import { ArrowLeft, ArrowRight, Check, Info, Lock } from "lucide-react";
import type React from "react";

/**
 * Wat de eigenaar na het indienen ziet: de stand van zijn aanvraag, of de mededeling dat hij
 * afgesloten is.
 *
 * <p><b>Ingediend is niet afgehandeld, en afgehandeld is niet "dier opgenomen".</b> Dat onderscheid
 * staat hier met zoveel woorden, want het is precies wat iemand in deze situatie wil weten en waar
 * een status in hoofdletters niets over zegt.
 *
 * <p><b>Een afgesloten aanvraag toont geen inhoud meer.</b> Niet de dieren, niet de antwoorden, niet
 * de documenten — alleen dat hij afgesloten is en dat een nieuwe aanvraag met hetzelfde adres kan.
 * Dat is wat "de toegang wordt beëindigd" hoort te betekenen.
 */

/** De statussen in woorden die een eigenaar iets zeggen. */
const UITLEG: Record<string, { kop: string; tekst: string }> = {
  NIEUW: {
    kop: "Ontvangen",
    tekst: "Uw aanvraag staat klaar bij het asiel. Zij kijken ernaar zodra het hun lukt.",
  },
  IN_BEHANDELING: {
    kop: "In behandeling",
    tekst: "Het asiel is met uw aanvraag bezig en neemt contact met u op.",
  },
  ACTIE_VEREIST: {
    kop: "Aanvulling gevraagd",
    tekst: "Het asiel heeft nog iets van u nodig. U kunt uw aanvraag hieronder aanvullen; "
      + "lukt dat niet, dan nemen zij contact met u op.",
  },
  COMPLEET: {
    kop: "Compleet",
    tekst: "Uw aanvraag is compleet. Het asiel beoordeelt hem en laat u weten wat er volgt.",
  },
  GEPLAND: {
    kop: "Afspraak gepland",
    tekst: "Er is een afspraak gepland. Het asiel heeft u daarover bericht.",
  },
  ONBEKEND: {
    kop: "Ontvangen",
    tekst: "Uw aanvraag is ingediend. De actuele stand is op dit moment niet op te halen.",
  },
};

/**
 * De kop en uitleg die bij een stand horen.
 *
 * <p><b>ACTIE_VEREIST zonder dat een mens erom vroeg is een interne signalering.</b> Het asiel zet
 * die status ook vanzelf zodra een dossier niet compleet is. Dan heeft niemand iets gevraagd, en
 * "het asiel heeft nog iets van u nodig" is onwaar -- het stuurt iemand op pad voor niets
 * (Maarten, 26-10-2026). Hij leest dan hetzelfde als bij een nieuwe aanvraag, want dat is wat er
 * voor hem aan de hand is.
 */
export function standTekst(status?: string, aanvullingGevraagd?: boolean): { kop: string; tekst: string } {
  if (status === "ACTIE_VEREIST" && !aanvullingGevraagd) {
    return UITLEG.NIEUW;
  }
  return UITLEG[status ?? "ONBEKEND"] ?? UITLEG.ONBEKEND;
}

export function AfstandIngediend({ status, opvangNaam, dierNamen, onAanvullen, overzicht }: {
  status: string;
  opvangNaam: string;
  dierNamen: string[];
  /** Bieden we aan de aanvraag aan te vullen? Alleen wanneer het asiel erom vraagt. */
  onAanvullen?: () => void;
  /** Wat hij heeft ingediend, om terug te kijken. */
  overzicht?: React.ReactNode;
}) {
  // Aanvulling gevraagd: dan is doorklikken het enige wat hij wil, en moet dat niet verstopt zitten
  // achter een telefoontje van het asiel.
  const aanvullen = status === "ACTIE_VEREIST" && onAanvullen != null;
  /*
   * ACTIE_VEREIST zonder dat een mens erom vroeg is een interne signalering.
   *
   * Het asiel zet die status ook vanzelf zodra een dossier niet compleet is. Dan heeft niemand iets
   * gevraagd, en "het asiel heeft nog iets van u nodig" is dus onwaar -- het stuurt iemand op pad
   * voor niets (Maarten, 26-10-2026). Hij leest dan hetzelfde als bij een nieuwe aanvraag, want dat
   * is wat er voor hem aan de hand is.
   */
  const uitleg = status === "ACTIE_VEREIST" && !aanvullen
    ? UITLEG.NIEUW
    : (UITLEG[status] ?? UITLEG.ONBEKEND);
  return (
    <section className="afst__paneel afst__afgerond">
      <span className="afst__afgerond-teken" aria-hidden="true"><Check size={26} /></span>
      <h3>Uw afstandsaanvraag is ontvangen</h3>
      <p className="afst__paneeltekst">
        Uw aanvraag {dierNamen.length > 0 ? `met ${dierNamen.join(" en ")} ` : ""}is ingediend bij
        {opvangNaam ? ` ${opvangNaam}` : " het asiel"}. Het asiel beoordeelt de gegevens en neemt
        contact met u op als er aanvullende informatie nodig is.
      </p>

      <p className="afst__stand">{uitleg.kop}</p>
      <p className="afst__paneeltekst" style={{ marginTop: 14 }}>{uitleg.tekst}</p>

      {aanvullen && (
        <button type="button" className="afst__knop" style={{ width: "auto", marginTop: 18 }}
                onClick={onAanvullen}>
          Aanvraag aanvullen
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      )}

      {overzicht}

      <p className="afst__tip">
        <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
        Indienen is geen definitieve afstand en geen toezegging van opname. U kunt uw aanvraag volgen
        zolang deze in behandeling is; na verwerking of annulering wordt de portaaltoegang beëindigd.
      </p>
    </section>
  );
}

export function AfstandAfgesloten({ onOpnieuw }: { onOpnieuw: () => void }) {
  return (
    <section className="afst__paneel afst__afgerond">
      <span className="afst__afgerond-teken afst__afgerond-teken--slot" aria-hidden="true">
        <Lock size={24} />
      </span>
      <h3>Deze aanvraag is afgesloten</h3>
      <p className="afst__paneeltekst">
        De afstandsaanvraag is door het asiel verwerkt of geannuleerd. U kunt de gegevens niet langer
        via dit portaal inzien of wijzigen.
      </p>

      <p className="afst__tip">
        <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
        Wilt u op een later moment opnieuw een dier aanmelden? Dan kunt u met hetzelfde e-mailadres
        een nieuwe aanvraag starten.
      </p>

      <button type="button" className="afst__knop afst__knop--rustig" onClick={onOpnieuw}
              style={{ width: "auto", marginTop: 20 }}>
        <ArrowLeft size={18} aria-hidden="true" />
        Terug naar aanmelden
      </button>
    </section>
  );
}
